import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { NavigationModule } from '../../Components/Navigation/navigationConfig';

interface TopDockOffcanvasProps {
  isOpen: boolean;
  toggle: () => void;
  modules: NavigationModule[];
}

/**
 * TopDockOffcanvas — pure-Tailwind slide-out from the right (Bootstrap
 * `.offcanvas` removed 2026-06-22). Panel mechanics are Tailwind; the inner
 * content classes (top-dock-offcanvas-*, offcanvas-header/body) are styled by
 * the self-contained top-dock-layout.scss, so this survives Bootstrap removal.
 */
const TopDockOffcanvas: React.FC<TopDockOffcanvasProps> = ({ isOpen, toggle, modules }) => {
  const location = useLocation();

  const isActive = (route: string): boolean => {
    if (route === '/dashboard' || route === '/dashboard-modern') {
      return location.pathname === route;
    }
    return location.pathname === route || location.pathname.startsWith(route + '/');
  };

  const handleItemClick = () => {
    toggle(); // Close offcanvas when item is clicked
  };

  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') toggle();
    };
    document.addEventListener('keydown', onEsc);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEsc);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, toggle]);

  return createPortal(
    <>
      <div
        className="tw-fixed tw-inset-0 tw-z-[1040] tw-bg-black/50 tw-transition-opacity tw-duration-300"
        style={{ opacity: isOpen ? 1 : 0, pointerEvents: isOpen ? 'auto' : 'none' }}
        onClick={toggle}
        aria-hidden="true"
      />
      <div
        className="top-dock-offcanvas tw-fixed tw-inset-y-0 tw-right-0 tw-z-[1045] tw-flex tw-w-[400px] tw-max-w-[85%] tw-flex-col tw-bg-white tw-shadow-xl tw-transition-transform tw-duration-300 tw-ease-in-out"
        style={{ transform: isOpen ? 'translateX(0)' : 'translateX(100%)' }}
        tabIndex={-1}
        aria-labelledby="top-dock-offcanvas-title"
        aria-hidden={!isOpen}
      >
        <div className="offcanvas-header tw-flex tw-items-center tw-justify-between">
          <h5 className="offcanvas-title tw-mb-0" id="top-dock-offcanvas-title">
            Modules
          </h5>
          <button
            type="button"
            className="tw-border-0 tw-bg-transparent tw-text-2xl tw-leading-none tw-text-slate-500 tw-cursor-pointer hover:tw-text-slate-700"
            aria-label="Close"
            onClick={toggle}
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
        <div className="offcanvas-body tw-overflow-y-auto">
          <ul className="top-dock-offcanvas-list">
            {modules.map((module) => {
              const active = isActive(module.route);
              return (
                <li key={module.id} className="top-dock-offcanvas-item">
                  <Link
                    to={module.route}
                    className={`top-dock-offcanvas-link ${active ? 'active' : ''}`}
                    onClick={handleItemClick}
                    aria-current={active ? 'page' : undefined}
                    aria-label={module.label}
                  >
                    <i className={`top-dock-offcanvas-icon ${module.icon}`} aria-hidden="true"></i>
                    <span className="top-dock-offcanvas-label">{module.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </>,
    document.body
  );
};

export default TopDockOffcanvas;
